import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import Form from '../../Forms/Form';
import { processCardNumberInput, processCVVInput, processExpirationDateInput } from '../../Forms/cardUtils';
import { FaCreditCard, FaLock } from "react-icons/fa6";
import { demoCardHelperText } from '../../../utils/demoPayments';

const CardAuthorizationStep = forwardRef(({ 
    onAuthorize,
    isAuthorizing,
    amountCents,
    currency,
    showToast
}, ref) => {
    const [cardholder, setCardholder] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [exp, setExp] = useState('');
    const [cvc, setCvc] = useState('');
    const [errors, setErrors] = useState({});
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);

    const cardholderRef = useRef(null);
    const cardNumberRef = useRef(null);
    const expRef = useRef(null);
    const cvcRef = useRef(null);

    const formattedAmount = (amountCents > 0)
        ? `${(amountCents / 100).toFixed(2)} ${currency}`
        : '';

    const validateForm = () => {
        const newErrors = {};
        
        const trimmedCardholder = cardholder.trim();
        if (!trimmedCardholder) {
            newErrors.cardholder = 'Cardholder name is required';
        } else if (trimmedCardholder.length < 2) {
            newErrors.cardholder = 'Name must be at least 2 characters';
        }
        
        if (!cardNumber) {
            newErrors.cardNumber = 'Card number is required';
        } else if (cardNumber.replace(/\s/g, '').length < 13) {
            newErrors.cardNumber = 'Please enter a valid card number';
        }
        
        if (!exp) {
            newErrors.exp = 'Expiry date is required';
        } else if (exp.length < 5) {
            newErrors.exp = 'Please enter a valid expiry date (MM/YY)';
        }
        
        if (!cvc) {
            newErrors.cvc = 'CVV is required';
        } else if (cvc.length < 3) {
            newErrors.cvc = 'CVV must be 3-4 digits';
        }
        
        return newErrors;
    };

    const validateField = (fieldName, value) => {
        const formState = { cardholder, cardNumber, exp, cvc };
        formState[fieldName] = value;
        const tempState = { ...formState };
        
        const newErrors = {};
        if (fieldName === 'cardholder') {
            const trimmed = tempState.cardholder.trim();
            if (!trimmed) {
                newErrors.cardholder = 'Cardholder name is required';
            } else if (trimmed.length < 2) {
                newErrors.cardholder = 'Name must be at least 2 characters';
            }
        } else if (fieldName === 'cardNumber') {
            if (!tempState.cardNumber) {
                newErrors.cardNumber = 'Card number is required';
            } else if (tempState.cardNumber.replace(/\s/g, '').length < 13) {
                newErrors.cardNumber = 'Please enter a valid card number';
            }
        } else if (fieldName === 'exp') {
            if (!tempState.exp) {
                newErrors.exp = 'Expiry date is required';
            } else if (tempState.exp.length < 5) {
                newErrors.exp = 'Please enter a valid expiry date (MM/YY)';
            }
        } else if (fieldName === 'cvc') {
            if (!tempState.cvc) {
                newErrors.cvc = 'CVV is required';
            } else if (tempState.cvc.length < 3) {
                newErrors.cvc = 'CVV must be 3-4 digits';
            }
        }
        
        return newErrors[fieldName] || '';
    };

    const handleNameChange = (v) => {
        const value = v.target.value;
        setCardholder(value);
        if (attemptedSubmit) {
            const error = validateField('cardholder', value);
            setErrors(prev => ({ ...prev, cardholder: error }));
        }
    };

    const handleCardInputChange = (v) => {
        const value = processCardNumberInput(v.target.value);
        setCardNumber(value);
        if (attemptedSubmit) {
            const error = validateField('cardNumber', value);
            setErrors(prev => ({ ...prev, cardNumber: error }));
        }
    };

    const handleExpChange = (v) => {
        const value = processExpirationDateInput(v.target.value);
        setExp(value);
        if (attemptedSubmit) {
            const error = validateField('exp', value);
            setErrors(prev => ({ ...prev, exp: error }));
        }
    };

    const handleCvcChange = (v) => {
        const value = processCVVInput(v.target.value);
        setCvc(value);
        if (attemptedSubmit) {
            const error = validateField('cvc', value);
            setErrors(prev => ({ ...prev, cvc: error }));
        }
    };

    const focusFirstInvalidField = (validationErrors) => {
        const fieldOrder = ['cardholder', 'cardNumber', 'exp', 'cvc'];
        const refMap = {
            cardholder: cardholderRef,
            cardNumber: cardNumberRef,
            exp: expRef,
            cvc: cvcRef
        };

        for (const fieldName of fieldOrder) {
            if (validationErrors[fieldName]) {
                const fieldRef = refMap[fieldName];
                if (fieldRef?.current) {
                    fieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    fieldRef.current.focus();
                    break;
                }
            }
        }
    };

    const handleSubmit = async () => {
        if (isAuthorizing) return;

        setAttemptedSubmit(true);
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            focusFirstInvalidField(validationErrors);
            
            // Show toast notification for validation error
            if (showToast) {
                showToast({
                    message: 'Please complete all required fields correctly.',
                    type: 'error'
                });
            }
            return;
        }

        // Call parent handler with card data
        await onAuthorize({
            card: {
                number: cardNumber,
                expiration: exp,
                cvv: cvc
            },
            billing: {
                name: cardholder
            }
        });
    };

    // Expose submit method to parent via ref
    useImperativeHandle(ref, () => ({
        submit: handleSubmit
    }));

    return (
        <div className="step-container card-authorization-step">
            <div className="step-header">
                <h2 className='step-title'>Authorize Payment</h2>
                <p className="step-subtitle">
                    A temporary authorization may be placed on your card. You'll only be charged for energy you use.
                </p>
            </div>

            <div className="step-content">
                {formattedAmount && (
                    <div className="amount-display">
                        <span className="amount-label">Authorization Amount</span>
                        <span className="amount-value">{formattedAmount}</span>
                    </div>
                )}

                <Form
                    customClasses={['payment-form']}
                    fields={[
                        {
                            label: 'Cardholder Name',
                            type: 'text',
                            placeholder: 'John Doe',
                            name: 'cardholderName',
                            required: true,
                            value: cardholder,
                            onValueChange: handleNameChange,
                            onBlur: () => {
                                if (attemptedSubmit) {
                                    const error = validateField('cardholder', cardholder);
                                    setErrors(prev => ({ ...prev, cardholder: error }));
                                }
                            },
                            inputRef: cardholderRef,
                            error: errors.cardholder,
                            className: errors.cardholder ? 'error' : '',
                            enterKeyHint: 'next'
                        },
                        {
                            label: 'Card Number',
                            type: 'text',
                            placeholder: '1234 5678 9012 3456',
                            name: 'cardNumber',
                            value: cardNumber,
                            onValueChange: handleCardInputChange,
                            onBlur: () => {
                                if (attemptedSubmit) {
                                    const error = validateField('cardNumber', cardNumber);
                                    setErrors(prev => ({ ...prev, cardNumber: error }));
                                }
                            },
                            inputRef: cardNumberRef,
                            icon: <FaCreditCard />,
                            required: true,
                            error: errors.cardNumber,
                            className: errors.cardNumber ? 'error' : '',
                            // Only in a non-production build with
                            // VITE_DEMO_PAYMENTS_ENABLED=true (C-02).
                            helperText: demoCardHelperText(),
                            enterKeyHint: 'next'
                        },
                        {
                            group: 'cardDetails',
                            layout: 'grid-2col',
                            fields: [
                                {
                                    label: 'Expiry Date',
                                    type: 'text',
                                    placeholder: 'MM/YY',
                                    name: 'expiryDate',
                                    required: true,
                                    value: exp,
                                    onValueChange: handleExpChange,
                                    onBlur: () => {
                                        if (attemptedSubmit) {
                                            const error = validateField('exp', exp);
                                            setErrors(prev => ({ ...prev, exp: error }));
                                        }
                                    },
                                    inputRef: expRef,
                                    error: errors.exp,
                                    className: errors.exp ? 'error' : '',
                                    enterKeyHint: 'next'
                                },
                                {
                                    label: 'CVV',
                                    type: 'text',
                                    placeholder: '123',
                                    name: 'cvv',
                                    required: true,
                                    value: cvc,
                                    onValueChange: handleCvcChange,
                                    onBlur: () => {
                                        if (attemptedSubmit) {
                                            const error = validateField('cvc', cvc);
                                            setErrors(prev => ({ ...prev, cvc: error }));
                                        }
                                    },
                                    inputRef: cvcRef,
                                    error: errors.cvc,
                                    className: errors.cvc ? 'error' : '',
                                    enterKeyHint: 'done'
                                }
                            ]
                        }
                    ]}
                    submit={{ allowSubmit: true }}
                    onSubmit={handleSubmit}
                />

                <div className="secure-payment-notice">
                    <FaLock className="lock-icon" />
                    <span>Secure payment • Card details not stored</span>
                </div>
            </div>
        </div>
    );
});

CardAuthorizationStep.displayName = 'CardAuthorizationStep';

export default CardAuthorizationStep;
